import { Avatar, AvatarFallback } from '@repo/ui/avatar'
import { Button } from '@repo/ui/button'
import { DataProvider } from '@repo/ui/dataProvider'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/dropdownMenu'
import { useT } from '@repo/ui/hooks'
import { cn } from '@repo/ui/utils'
import { delay } from 'lodash'
import {
  ArrowLeftRightIcon,
  BarChart3,
  Check,
  ChevronsUpDown,
  HomeIcon,
  LogOutIcon,
  Settings,
  SlidersHorizontal,
  UserPenIcon,
  Users2Icon,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'

import { useAbility } from '@/ability/ability-context'
import { MeEditor } from '@/components/MeEditor'
import { useView } from '@/hooks'
import {
  buildSearchParam,
  isRoute,
  RouteNames,
  SearchParamsKeyNames,
  SearchParamsViewNames,
} from '@/routes'
import { RootState } from '@/store'
import { storeActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { CaseWorker, Organization, User } from '@/store/user'

type MeMenuProps = {
  caseWorker: CaseWorker
  className?: string
  organization: Organization
  user: User
  display: 'short' | 'full'
}

export function MeMenu(props: MeMenuProps) {
  const { caseWorker, organization, user } = props
  const dispatch = useStoreDispatch()
  const [_, setURLSearchParams] = useSearchParams()
  const location = useLocation()
  const pathname = useMemo(() => location.pathname, [location.pathname])
  const ability = useAbility()

  const [isMeEditorOpen, setIsMeEditorOpen] = useState(false)

  const { tc } = useT()
  const logout = () => {
    // NOTE: Due to a bug, we need to clear the URL search params before logout
    setURLSearchParams('')
    // NOTE: We need to delay the logout action to allow the URL search params to be cleared

    delay(() => {
      void dispatch(storeActions.logout())
    }, 100)
  }
  const view = useView()
  const { firstName, lastName } = caseWorker
  return (
    <>
      <MeEditor
        caseWorker={caseWorker}
        organization={organization}
        modalHandler={{ open: isMeEditorOpen, setOpen: setIsMeEditorOpen }}
      />
      <DropdownMenu>
        <Trigger {...props} />

        <DropdownMenuContent className="mx-auto min-w-[16rem] rounded-lg">
          <DropdownMenuGroup>
            <DropdownMenuLabel className="select-none p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="text-primary rounded-lg">
                    {firstName.charAt(0)}
                    {lastName?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {firstName} {lastName}
                  </span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>

            <DropdownMenuItem onClick={() => setIsMeEditorOpen(true)}>
              <UserPenIcon className="size-4" />
              {tc('editMyProfile', { cfl: true })}
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {isRoute(RouteNames.Admin, pathname) && (
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to={view.dashboard().route}>
                  <HomeIcon className="size-4" />
                  {tc('home', { cfl: true })}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </DropdownMenuGroup>
          )}

          {ability.can('accessRoute', 'admin') && (
            <DropdownMenuGroup>
              <DropdownMenuLabel className="flex items-center gap-2">
                {organization.name}
              </DropdownMenuLabel>

              <DropdownMenuItem asChild>
                <Link to={RouteNames.Admin}>
                  <SlidersHorizontal className="size-4" />
                  {tc('settings', { cfl: true })}
                  {isRoute(RouteNames.Admin, pathname) && <Check className="size-4" />}
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <Link
                  to={
                    RouteNames.Admin +
                    buildSearchParam(
                      SearchParamsKeyNames.View,
                      SearchParamsViewNames.BENEFICIARY_LIST,
                    )
                  }
                >
                  <Users2Icon className="size-4" />
                  {tc('beneficiaries', { cfl: true })}
                </Link>
              </DropdownMenuItem>

              {ability.can('manage', 'Insights') && (
                <DropdownMenuItem asChild>
                  <Link
                    to={
                      RouteNames.Admin +
                      buildSearchParam(
                        SearchParamsKeyNames.View,
                        SearchParamsViewNames.INSIGHTS_LIST,
                      )
                    }
                  >
                    <BarChart3 className="size-4" />
                    {tc('showAnalyticsShortcut', { cfl: true })}
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
          )}

          {!isRoute(RouteNames.Root, pathname) && (
            <DataProvider
              selectors={[(state: RootState) => state.user.organizations]}
              render={(organizations) => {
                if (organizations.length <= 1) return null
                return (
                  <>
                    <DropdownMenuSeparator />

                    <DropdownMenuGroup>
                      <DropdownMenuItem asChild>
                        <Link to={RouteNames.Root}>
                          <ArrowLeftRightIcon className="size-4" />
                          {tc('switchOrganization', { cfl: true })}
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </>
                )
              }}
            />
          )}

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuItem onClick={logout}>
              <LogOutIcon className="size-4" />
              {tc('logout', { cfl: true })}
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

function Trigger({ className, caseWorker, display, organization, user }: MeMenuProps) {
  const ability = useAbility()
  return (
    <div className={cn('flex w-full flex-1', className)}>
      <DropdownMenuTrigger asChild>
        {display === 'full' ? (
          <div className={cn('flex w-full flex-1', className)}>
            <Button className="data-[state=open]:bg-primary-900 w-full py-6">
              <Avatar className="mr-4 h-8 w-8 rounded-lg">
                <AvatarFallback className="text-primary rounded-lg">
                  {caseWorker.firstName.charAt(0)}
                  {caseWorker.lastName?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {caseWorker.firstName} {caseWorker.lastName}
                </span>
                <span className="text-primary-300 truncate text-xs">
                  {organization.name ?? user.email}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </Button>
          </div>
        ) : (
          <Button
            variant={ability.can('accessRoute', 'admin') ? 'default' : 'outline'}
            size="icon"
            className={cn(
              'ring-offset-inherit focus-visible:ring-0 focus-visible:ring-offset-0',
              ability.can('accessRoute', 'admin') ? '' : 'data-[state=open]:bg-primary-100',
            )}
          >
            <Settings className="size-4" />
          </Button>
        )}
      </DropdownMenuTrigger>
    </div>
  )
}
